// =============================================================================
// domain/coachDimensions.ts — three of the four reads that are not fixable leaks.
// -----------------------------------------------------------------------------
// Risk of ruin lives in `riskOfRuin.ts`; the other three are here. The old
// client's tour calls them "four more lenses" and the distinction from Spot it
// is the point: a LEAK has a dollar cost and a rule that closes it. These do
// not. They describe the edge rather than name a habit, so none of them ever
// gets a Fix-it button.
//
// ── 🚨 EVERY ONE HAS A MINIMUM, AND BELOW IT THE ANSWER IS `null` ──
//
// Three trades on one pattern × exit pairing is not a pairing, it is three
// trades. The old client's floors are reproduced exactly (5 for the E-ratio, 3
// for a confluence, 3 for a pairing) because they are the difference between
// "your Break-and-Retest × Trail pairing is the engine" and a sentence about
// noise that a trader would then rearrange their week around.
// =============================================================================

import { median } from './leakSequence';
// ⚠ THE ONE RULE FOR "which stored ids count", shared with the Confluence Edge
// card. A fourth hand-rolled copy of it is what this card exists to remove.
import type { ResolvedMark } from './confluenceEdge';

/** The old client's floor before an E-ratio means anything. */
export const ERATIO_MIN_SAMPLE = 5;
/** Trades on one confluence before it may be ranked. */
export const CONFLUENCE_MIN_SAMPLE = 3;
/** Trades in one pattern × exit cell before it may be named. */
export const PAIRING_MIN_SAMPLE = 3;

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const mean = (xs: readonly number[]): number | null =>
  (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

// ---------------------------------------------------------------------------
// 2 · Edge quality — how much of each move you keep, and whether there is an edge
// ---------------------------------------------------------------------------

export interface EdgeTrade {
  maePoints?: number | null;
  mfePoints?: number | null;
}

export interface EdgeQuality {
  /** avg MFE ÷ avg MAE. Above ~1.2 is a real edge. Null below the floor. */
  eRatio: number | null;
  avgMae: number | null;
  avgMfe: number | null;
  /** Trades carrying BOTH excursions — the only ones the ratio can use. */
  n: number;
}

/**
 * ⚠ THE DIVIDE-BY-NEARLY-ZERO GUARD IS THE OLD CLIENT'S, AND IT IS LOAD-BEARING.
 * An average MAE of 0.05 points produces an E-ratio in the hundreds, which reads
 * as a spectacular edge and is really a rounding artefact on trades that barely
 * moved against the trader.
 */
export function edgeQuality(trades: readonly EdgeTrade[]): EdgeQuality {
  const maes: number[] = [];
  const mfes: number[] = [];
  for (const t of trades) {
    const mae = numOrNull(t.maePoints);
    const mfe = numOrNull(t.mfePoints);
    // ⚠ BOTH, or neither. Averaging MAE over one population and MFE over a
    // larger one makes a ratio out of two different books.
    if (mae === null || mfe === null) continue;
    maes.push(Math.abs(mae));
    mfes.push(Math.abs(mfe));
  }
  const avgMae = mean(maes);
  const avgMfe = mean(mfes);
  const n = maes.length;
  const eRatio = (n >= ERATIO_MIN_SAMPLE && avgMae !== null && avgMae > 0.1 && avgMfe !== null)
    ? avgMfe / avgMae
    : null;
  return { eRatio, avgMae, avgMfe, n };
}

// ---------------------------------------------------------------------------
// 3 · Which confluences carry the edge
// ---------------------------------------------------------------------------

export interface ConfluenceTrade {
  confluences?: Record<string, unknown> | null;
  rrRealized?: number | null;
}

export interface ConfluenceRank {
  id: string;
  name: string;
  medianR: number;
  n: number;
}

/**
 * Median realized R per confluence, best first.
 *
 * ⚠ THE MEDIAN, NOT THE MEAN, and the old client chooses it deliberately. One
 * runner on a confluence tagged four times drags a mean to a number the trader
 * will never see again; the median says what a TYPICAL trade with that read
 * returned, which is the question being asked.
 *
 * ⚠ A trade may carry several confluences and counts toward each. They are not
 * a partition — the trader ticked all of them.
 *
 * ── 🚨 IT TAKES THE MARKS, NOT THE TRADES' RAW MAP, AND THAT IS THE FIX ──
 *
 * This walked `Object.entries(t.confluences)` itself and skipped anything where
 * `on !== true`. The writer stores `{ id: { active: true } }` — an OBJECT — so
 * that comparison was false for every row this client has ever saved, and the
 * Coach's Report confluence panel rendered NOTHING. Every other surface reads
 * the same column correctly because they go through `activeConfluenceIds`,
 * which accepts the object form and the legacy bare boolean alike.
 *
 * ⚠ AND ITS OWN TESTS COULD NOT SEE IT. They built fixtures as `{ vwap: true }`
 * — a shape production never writes — so they exercised data that is not in the
 * database, passed, and said nothing.
 *
 * ▶ So the traversal is gone. The caller supplies a marks function, exactly as
 * `confluenceEdge` takes one, and there is ONE rule (`marksOf`) deciding which
 * stored ids count rather than a fourth hand-rolled copy of it.
 *
 * ⚠ THAT RULE ALSO DROPS CATEGORY PARENTS, and accepting the object shape
 * WITHOUT it would have been worse than the empty panel: a confluence with
 * sub-confluences is a heading, its subs carry the signal, and ranking both
 * counts one observation twice. This ranking is what the paid AI coach is told
 * to name exactly — it would have recommended leaning into a heading, or into
 * the raw code of a deleted tool.
 */
export function confluenceRanking(
  trades: readonly ConfluenceTrade[],
  marks: (t: ConfluenceTrade) => readonly ResolvedMark[],
  limit = 3,
): ConfluenceRank[] {
  const rs = new Map<string, { name: string; rs: number[] }>();
  for (const t of trades) {
    const r = numOrNull(t.rrRealized);
    if (r === null) continue;
    // ⚠ DEDUPED BY ID, as `confluenceEdge` does — and this CANNOT FIRE through
    // the shared reader, which is worth saying rather than implying otherwise.
    // `activeConfluenceIds` returns a Set, so `marksOf` can never emit one id
    // twice; the sibling's comment claims it catches "the same id in both stored
    // shapes" and that is already collapsed upstream.
    //
    // ▶ It stays because `marks` is CALLER-SUPPLIED. A future caller composing
    // marks some other way could hand over duplicates, and one trade counting
    // twice toward one confluence is a silent inflation of `n` and a shifted
    // median — not a crash. MEASURED: deleting these two lines breaks no test,
    // because nothing can currently produce the input they guard against.
    const seen = new Set<string>();
    for (const m of marks(t)) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      const g = rs.get(m.id) ?? { name: m.name, rs: [] };
      g.rs.push(r);
      rs.set(m.id, g);
    }
  }

  const out: ConfluenceRank[] = [];
  for (const [id, g] of rs) {
    if (g.rs.length < CONFLUENCE_MIN_SAMPLE) continue;
    const m = median(g.rs);
    if (m === null) continue;
    out.push({ id, name: g.name, medianR: m, n: g.rs.length });
  }
  return out.sort((a, b) => b.medianR - a.medianR).slice(0, limit);
}

// ---------------------------------------------------------------------------
// 4 · Pattern × exit-model pairings
// ---------------------------------------------------------------------------

export interface PairingTrade {
  patternId?: string | null;
  exitModelId?: string | null;
  rrRealized?: number | null;
}

export interface Pairing {
  patternId: string;
  exitModelId: string;
  pattern: string;
  exit: string;
  avgR: number;
  n: number;
}

export interface Pairings { best: Pairing | null; worst: Pairing | null }

/**
 * The best and worst pattern × exit combinations.
 *
 * ⚠ A PAIRING IS A CELL, not a pattern and not an exit model. The old client's
 * framing is that the same setup taken with a different exit is a different
 * trade — which is exactly the finding a trader cannot get from either
 * leaderboard alone.
 *
 * ⚠ WHEN ONE CELL QUALIFIES IT IS THE BEST AND NOTHING IS THE WORST. Reporting
 * the same pairing as both would read as a contradiction, and the old client
 * drops the worst for this reason.
 */
export function pairings(
  trades: readonly PairingTrade[],
  patternName: (id: string) => string | null,
  exitName: (id: string) => string | null,
): Pairings {
  const cells = new Map<string, number[]>();
  for (const t of trades) {
    const p = t.patternId;
    const e = t.exitModelId;
    if (!p || !e) continue;
    const r = numOrNull(t.rrRealized);
    if (r === null) continue;
    const k = `${p}|${e}`;
    const list = cells.get(k) ?? [];
    list.push(r);
    cells.set(k, list);
  }

  let best: Pairing | null = null;
  let worst: Pairing | null = null;
  for (const [k, list] of cells) {
    if (list.length < PAIRING_MIN_SAMPLE) continue;
    const avg = mean(list)!;
    const [p, e] = k.split('|') as [string, string];
    const cell: Pairing = {
      patternId: p, exitModelId: e,
      pattern: patternName(p) ?? 'Pattern',
      exit: exitName(e) ?? 'Exit',
      avgR: avg, n: list.length,
    };
    if (!best || avg > best.avgR) best = cell;
    if (!worst || avg < worst.avgR) worst = cell;
  }
  if (best && worst && best.patternId === worst.patternId
      && best.exitModelId === worst.exitModelId) {
    worst = null;
  }
  return { best, worst };
}
